import { useState } from 'react';
import { Info, KeyRound, Link as LinkIcon, Lock, Mail, Users2 } from 'lucide-react';
import {
  useAuthMethods,
  useEnableAuthMethod,
  useDisableAuthMethod,
} from '../../hooks/useAuthMethods';
import { ProviderIcon } from '../oauth/ProviderIcon';
import type { AuthMethodConfig, AuthMethod } from '../../types/authMethods';
import { isOAuthMethod } from '../../types/authMethods';
import type { OAuthProvider } from '../../types/oauth';
import { Badge, Button } from '../../ui';
import './AuthMethodsSettings.css';

interface AuthMethodsSettingsProps {
  projectId: string;
  userRole?: 'viewer' | 'member' | 'admin' | 'owner';
  onConfigureOAuth?: (provider: OAuthProvider) => void;
}

export function AuthMethodsSettings({
  projectId,
  userRole = 'member',
  onConfigureOAuth,
}: AuthMethodsSettingsProps) {
  const { data, isLoading, error } = useAuthMethods(projectId);
  const enableMethod = useEnableAuthMethod();
  const disableMethod = useDisableAuthMethod();
  const [pendingToggle, setPendingToggle] = useState<AuthMethod | null>(null);

  const canManage = userRole === 'admin' || userRole === 'owner';

  const handleToggle = async (method: AuthMethodConfig) => {
    if (!canManage) return;

    setPendingToggle(method.method);
    try {
      if (method.enabled) {
        await disableMethod.mutateAsync({ projectId, method: method.method });
      } else {
        await enableMethod.mutateAsync({ projectId, method: method.method });
      }
    } catch (err) {
      console.error('Failed to toggle auth method:', err);
    } finally {
      setPendingToggle(null);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-methods-settings">
        <div className="auth-methods-settings__loading">
          <div className="spinner" />
          <p>Loading authentication methods...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-methods-settings">
        <div className="auth-methods-settings__error">
          <p>Failed to load authentication methods</p>
          <p className="auth-methods-settings__error-detail">{error.message}</p>
        </div>
      </div>
    );
  }

  const { auth_methods = [] } = data || {};

  const passwordlessMethods = auth_methods.filter(m =>
    ['email_password', 'magic_link', 'email_otp'].includes(m.method)
  );
  const oauthMethods = auth_methods.filter(m => isOAuthMethod(m.method));

  return (
    <div className="auth-methods-settings">
      <div className="auth-methods-settings__header">
        <h3>Authentication Methods</h3>
        <p className="auth-methods-settings__muted">
          Choose which authentication methods are available for your end users.
        </p>
      </div>

      <div className="auth-methods-section">
        <h4 className="auth-methods-section__title">
          <span className="auth-methods-section__icon" aria-hidden="true">
            <Mail size={16} />
          </span>
          Email Authentication
        </h4>
        <div className="auth-methods-list">
          {passwordlessMethods.map(method => (
            <AuthMethodCard
              key={method.method}
              method={method}
              canManage={canManage}
              isPending={pendingToggle === method.method}
              onToggle={() => handleToggle(method)}
            />
          ))}
        </div>
      </div>

      <div className="auth-methods-section">
        <h4 className="auth-methods-section__title">
          <span className="auth-methods-section__icon" aria-hidden="true">
            <Users2 size={16} />
          </span>
          Social Login Providers
        </h4>
        <p className="auth-methods-section__description">
          OAuth providers must be configured in the Integrations tab before they can be enabled.
        </p>
        <div className="auth-methods-list">
          {oauthMethods.map(method => (
            <AuthMethodCard
              key={method.method}
              method={method}
              canManage={canManage}
              isPending={pendingToggle === method.method}
              onToggle={() => handleToggle(method)}
              onConfigure={
                onConfigureOAuth
                  ? () => onConfigureOAuth(method.method as OAuthProvider)
                  : undefined
              }
            />
          ))}
        </div>
      </div>

      {!canManage && (
        <div className="auth-methods-settings__readonly-notice">
          <Info size={14} aria-hidden="true" />
          <span>You need admin permissions to change authentication settings.</span>
        </div>
      )}
    </div>
  );
}

interface AuthMethodCardProps {
  method: AuthMethodConfig;
  canManage: boolean;
  isPending: boolean;
  onToggle: () => void;
  onConfigure?: () => void;
}

function AuthMethodCard({
  method,
  canManage,
  isPending,
  onToggle,
  onConfigure,
}: AuthMethodCardProps) {
  const isOAuth = isOAuthMethod(method.method);
  const needsConfiguration = isOAuth && !method.configured;
  const canEnable = !needsConfiguration || method.enabled;

  return (
    <div
      className={`auth-method-card ${method.enabled ? 'auth-method-card--enabled' : ''} ${
        needsConfiguration ? 'auth-method-card--needs-config' : ''
      }`}
    >
      <div className="auth-method-card__icon">
        {isOAuth ? (
          <ProviderIcon provider={method.method as OAuthProvider} size="md" />
        ) : (
          <MethodIcon method={method.method} />
        )}
      </div>

      <div className="auth-method-card__content">
        <div className="auth-method-card__header">
          <h5 className="auth-method-card__name">{method.display_name}</h5>
          {needsConfiguration && <Badge tone="warning">Not Configured</Badge>}
          {method.enabled && method.configured && <Badge tone="success">Active</Badge>}
        </div>
        <p className="auth-method-card__description">{method.description}</p>
      </div>

      <div className="auth-method-card__actions">
        {needsConfiguration && canManage && onConfigure && (
          <Button variant="secondary" size="sm" onClick={onConfigure}>
            Configure
          </Button>
        )}
        {canManage && (
          <label
            className={`auth-method-toggle ${!canEnable ? 'auth-method-toggle--disabled' : ''}`}
          >
            <input
              type="checkbox"
              checked={method.enabled}
              onChange={onToggle}
              disabled={isPending || !canEnable}
            />
            <span className="auth-method-toggle__slider" />
          </label>
        )}
      </div>
    </div>
  );
}

function MethodIcon({ method }: { method: AuthMethod }) {
  switch (method) {
    case 'email_password':
      return <Lock size={22} aria-hidden="true" />;
    case 'magic_link':
      return <LinkIcon size={22} aria-hidden="true" />;
    case 'email_otp':
      return <KeyRound size={22} aria-hidden="true" />;
    default:
      return null;
  }
}
